import { action, autorun, makeObservable, observable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';

export class GroupInfoController {
  rootStore: RootStore;
  pageLoading = false;

  async requestInitialData(groupId: string, agentView = true): Promise<void> {
    this.setPageLoadingStatus(true);
    const initialRequests = [
      this.rootStore.groupStore.getGroupById(groupId),
      this.rootStore.enrollmentStore.getAllActiveEnrollmentPeriodsByGroupId(groupId),
    ];
    if (agentView) {
      initialRequests.push(this.rootStore.agentsStore.requestAgentsAndAssignees());
    }
    await Promise.all(initialRequests);
    runInAction(() => {
      this.setPageLoadingStatus(false);
    });
  }

  setPageLoadingStatus(loadingStatus: boolean): void {
    this.pageLoading = loadingStatus;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    // Update groupSidebarStore and in enrollmentCardStore
    autorun(() => {
      this.rootStore.groupSidebarStore.getGroupLocally({ ...this.rootStore.groupStore.group });
    });

    // Update EnrollmentCardStore
    autorun(() => {
      this.rootStore.enrollmentCardStore.getGroupLocally({ ...this.rootStore.groupStore.group });
    });

    // Update groupInfoModalStore
    autorun(() => {
      this.rootStore.groupInfoModalStore.updateFormWithData({
        contactInfo: { ...this.rootStore.groupStore.group.contactInfo },
        name: this.rootStore.groupStore.group.name,
      });
    });

    // Update browsingModalStore
    autorun(() => {
      if (this.rootStore.groupStore.group.id) {
        this.rootStore.browsingModalsStore.updateFormWithData(
          this.rootStore.groupStore.group.contactInfo.primaryAddress.zip || '',
        );
      }
    });

    // Update groupNotesModalStore
    autorun(() => {
      this.rootStore.groupNotesModalStore.updateFormWithData(
        this.rootStore.groupStore.group.notes
          // Remove only leading spaces after a line break
          ?.replace(/\n[ \t]+/g, '\n')
          // Ensure a single empty line between points
          .replace(/\n{3,}/g, '\n\n') || '',
      );
    });

    autorun(() => {
      const accountRepId = this.rootStore.groupStore.group.accountRepresentative?.id;
      this.rootStore.assignAgentsModalStore.updateLocalSelectedAccountRep(
        accountRepId === '' ? 'null' : accountRepId || '',
      );
    });

    autorun(() => {
      this.rootStore.assignAgentsModalStore.updateLocalAssignees(
        this.rootStore.groupStore.group.assigned || [],
        this.rootStore.agentsStore.assignees,
      );
    });

    autorun(() => {
      this.rootStore.assignAgentsModalStore.updateLocalAccountReps(this.rootStore.agentsStore.accountRepresentatives);
    });

    autorun(() => {
      this.rootStore.groupProposalsTableStore.getPeriodsLocally(
        this.rootStore.enrollmentStore.enrollmentPeriodsByGroupId,
      );
      this.rootStore.browsingModalsStore.setUnavailableData(this.rootStore.enrollmentStore.enrollmentPeriodsByGroupId);
    });

    makeObservable(this, {
      pageLoading: observable,
      requestInitialData: action.bound,
      setPageLoadingStatus: action.bound,
    });
  }
}
